import { CalenderIcon } from 'assests/GmsIcons';
import  React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LAST_30_DAYS, LAST_3_MONTHS, LAST_7_DAYS, LAST_MONTH, LAST_YEAR_1M, P1D, P1M, P1W, PT2H, THIS_YEAR_1M, THIS_YEAR_1W, TIME_ZONE_MESSAGE, TODAY, YESTERDAY } from '../AnalyticsConstants';
import { TDateSelectionMenuProps } from '../analytics';
import { formatDate, formatDateRange, formatDateWithMoment, getTodaysDateWithTimezone, lowerBoundDateFormat, upperBoundDateformat } from '../MetricsHelper';
import useClickOutside from 'Components/GMS/CustomHooks/useClickOutside';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import moment from 'moment';
import { getMonthAbbreviation } from '../AnalyticsHelper';

export const DateSelectionMenu = ({ setAnalyticsPayload, analyticsPayload, selectedItem, setSelectedItem, timezone, selectedTimezone, selectedRange, setSelectedRange}: TDateSelectionMenuProps) => {
    const { showMenu, ref, handleClick } = useClickOutside();
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (selectedRange) {
            handleSelection(selectedRange, getCustomDateRange(selectedRange));
        }
    }, [selectedTimezone]);

    const getCustomDateRange = (dateRange: string): string => {
        const today = moment().tz(selectedTimezone.name);
        if (dateRange === TODAY) {
            const currentDate = today.clone();
            const tomo = today.clone().add(1, 'day');
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(currentDate.toDate(), selectedTimezone.name), formatDateWithMoment(tomo)], granularity: PT2H};
            setAnalyticsPayload(updatedState);
            return getTodaysDateWithTimezone(selectedTimezone.name);
        } else if (dateRange === YESTERDAY) {
            const currentDate = moment().tz(selectedTimezone.name);
            const yesterday = currentDate.clone().subtract(1, 'day');
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(yesterday.toDate(), selectedTimezone.name), formatDate(currentDate.toDate(), selectedTimezone.name)], granularity: PT2H};
            setAnalyticsPayload(updatedState);
            const month: string = yesterday.format('MMMM');
            const date = yesterday.date(); 
            const formattedDate = `${getMonthAbbreviation(month)} ${date}`;
            return formattedDate;
        } else if (dateRange === LAST_7_DAYS) {
            const startDate = today.clone().subtract(6, 'days').toDate();
            const endDate = today.toDate();
            const updatedState = { ...analyticsPayload, dateRange: [lowerBoundDateFormat(startDate, selectedTimezone.name), formatDate(endDate, selectedTimezone.name)], granularity: P1D};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startDate, today.toDate(), dateRange, selectedTimezone.name);
        } else if (dateRange === LAST_30_DAYS) {
            const startDate = today.clone().subtract(30, 'days').toDate();
            const endDate = today.toDate();
            const updatedState = { ...analyticsPayload, dateRange: [lowerBoundDateFormat(startDate, selectedTimezone.name), formatDate(endDate, selectedTimezone.name)], granularity: P1D};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startDate, today.toDate(), dateRange, selectedTimezone.name);
        } else if (dateRange === LAST_MONTH) {
            const startOfMonth = today.clone().subtract(1, 'month').startOf('month').toDate();
            const endOfMonth = today.clone().subtract(1, 'month').endOf('month').toDate();
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(startOfMonth, selectedTimezone.name),upperBoundDateformat(endOfMonth, selectedTimezone.name)], granularity: P1D};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startOfMonth, endOfMonth, dateRange, selectedTimezone.name);
        } else if (dateRange === LAST_3_MONTHS) {
            const startOfMonth = today.clone().subtract(3, 'months').startOf('month').toDate();
            const endOfMonth = today.clone().subtract(1, 'months').endOf('month');
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(startOfMonth, selectedTimezone.name),upperBoundDateformat(endOfMonth.toDate(), selectedTimezone.name)], granularity: P1W};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startOfMonth, endOfMonth.toDate(), dateRange, selectedTimezone.name);
        } else if (dateRange === THIS_YEAR_1W) {
            const date: Date = new Date();
            const currentDate = moment(date).tz(selectedTimezone.name);
            const startOfYear = currentDate.clone().startOf('year').toDate();
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(startOfYear, selectedTimezone.name),formatDate(today.toDate(), selectedTimezone.name)], granularity: P1W};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startOfYear, today.toDate(), dateRange, selectedTimezone.name);
        }
        else if (dateRange === THIS_YEAR_1M) {
            const date: Date = new Date();
            const currentDate = moment(date).tz(selectedTimezone.name);
            const startOfYear = currentDate.clone().startOf('year').toDate();
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(startOfYear, selectedTimezone.name),formatDate(today.toDate(), selectedTimezone.name)], granularity: P1M};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startOfYear, today.toDate(), dateRange, selectedTimezone.name);
        }
        else if (dateRange === LAST_YEAR_1M) {
            const startOfYear = today.clone().subtract(1, 'year').startOf('year').toDate();
            const endOfYear = today.clone().subtract(1, 'year').endOf('year').toDate();
            const updatedState = { ...analyticsPayload, dateRange: [formatDate(startOfYear, selectedTimezone.name),upperBoundDateformat(endOfYear, selectedTimezone.name)], granularity: P1M};
            setAnalyticsPayload(updatedState);
            return formatDateRange(startOfYear, endOfYear, dateRange, selectedTimezone.name);
        }
        else {
            return 'Invalid date range';
        }
    }

    const handleSelection = (label: string, value: string) => {
        setSelectedItem({ label: label, value: value, });
    };
    
    return (
        <div ref={ref} className='tw-relative tw-bg-white tw-z-40'>  
            <div onClick={handleClick} className={`${showMenu ? 'tw-bg-[#EBECF0]' : ''} hover:tw-bg-[#EBECF0] tw-p-2 tw-rounded-[4px] tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-[6px]`}>
                <CalenderIcon/>
                <button className={`tw-text-sm tw-text-[#172B4D]`}>{selectedItem.label}<span className={`tw-text-sm tw-text-[#6B778C] tw-ms-2`}>{selectedItem.value ?  selectedItem.value + ` ${timezone}` : ''}</span></button> 
                {selectedItem.value ? <OverlayIcon placement="right" message={TIME_ZONE_MESSAGE}/> : null}
            </div>
            {showMenu ? (
                <div className={`tw-origin-top-left tw-absolute  tw-w-60 tw-mx-3 tw-my-1 tw-rounded-[4px] tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                    <div className={`tw-py-1 tw-text-left`} role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        <Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(TODAY)
                            handleSelection(TODAY, getCustomDateRange(TODAY));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{TODAY}</Link>
                        <Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(YESTERDAY);
                            handleSelection(YESTERDAY, getCustomDateRange(YESTERDAY));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{YESTERDAY}</Link>
                        <Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(LAST_7_DAYS);
                            handleSelection(LAST_7_DAYS, getCustomDateRange(LAST_7_DAYS));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{LAST_7_DAYS}</Link>
                        <Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(LAST_30_DAYS);
                            handleSelection(LAST_30_DAYS, getCustomDateRange(LAST_30_DAYS));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{LAST_30_DAYS}</Link>
                        <Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(LAST_MONTH);
                            handleSelection(LAST_MONTH, getCustomDateRange(LAST_MONTH));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{LAST_MONTH}</Link>
                        <Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(LAST_3_MONTHS);
                            handleSelection(LAST_3_MONTHS, getCustomDateRange(LAST_3_MONTHS));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{LAST_3_MONTHS}</Link>
                        {/* { 
                            <><Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(THIS_YEAR_1M);
                            handleSelection(THIS_YEAR_1M, getCustomDateRange(THIS_YEAR_1M));
                        } } className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{THIS_YEAR_1M}</Link><Link to="#" onClick={() => {
                            handleClick();
                            setSelectedRange(LAST_YEAR_1M);
                            handleSelection(LAST_YEAR_1M, getCustomDateRange(LAST_YEAR_1M));
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{LAST_YEAR_1M}</Link></>} */}
                        {/* <Link to="#" onClick={() => {
                            handleClick();
                            onOpen();
                            handleSelection("Custom", "");
                        }} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-black hover:tw-bg-[#F0F3FE] hover:tw-text-[#091CAA]`} role="menuitem">{CUSTOM}</Link> */}
                    </div>
                </div>                        
            ) : null}
        </div> 
    );
}